import React from 'react';
import SvgIcon from '@mui/material/SvgIcon';

// The home-type icons, one set for the whole site: the filter's Bostadstyp tabs, the area card's price boxes
// and the Färgkarta menu. The map markers use the filled shapes further down. Outline drawings on a 24×24 grid, stroked in
// the current colour. Change them here and every place follows.

export const HOME_TYPE_ICON_PATHS = {
    all: 'M5.5 4h4A1.5 1.5 0 0 1 11 5.5v4A1.5 1.5 0 0 1 9.5 11h-4A1.5 1.5 0 0 1 4 9.5v-4A1.5 1.5 0 0 1 5.5 4z'
        + 'M14.5 4h4A1.5 1.5 0 0 1 20 5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4A1.5 1.5 0 0 1 13 9.5v-4A1.5 1.5 0 0 1 14.5 4z'
        + 'M5.5 13h4a1.5 1.5 0 0 1 1.5 1.5v4A1.5 1.5 0 0 1 9.5 20h-4A1.5 1.5 0 0 1 4 18.5v-4A1.5 1.5 0 0 1 5.5 13z'
        + 'M14.5 13h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a1.5 1.5 0 0 1-1.5-1.5v-4a1.5 1.5 0 0 1 1.5-1.5z',
    apartment: 'M7.5 3h9A1.5 1.5 0 0 1 18 4.5v15a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 19.5v-15A1.5 1.5 0 0 1 7.5 3z'
        + 'M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2',
    house: 'M4 11l8-7 8 7v9H4zM10 20v-5h4v5',
    rowhouse: 'M2 12l5-5 5 5 5-5 5 5v8H2z',
    // Fritidshus: a little cabin (stuga) — a steeper gable and a shorter body than the villa.
    fritidshus: 'M5 13l7-9 7 9v7H5zM10.3 20v-5h3.4v5',
};

// 'apartment' | 'house' | 'rowhouse' | 'fritidshus' for a listing type (house++ and the Swedish names included).
export const homeTypeIconKey = (type) => {
    const normalized = String(type || '').toLowerCase().trim();
    if (normalized === 'apartment' || normalized === 'lägenhet' || normalized === 'lagenhet') return 'apartment';
    if (['rowhouse', 'house++', 'townhouse', 'radhus', 'parhus', 'par/radhus'].includes(normalized)) return 'rowhouse';
    if (normalized === 'fritidshus') return 'fritidshus';
    return 'house';
};

const pathFor = (key) => HOME_TYPE_ICON_PATHS[key] || HOME_TYPE_ICON_PATHS.house;

// Markup for HTML strings (area card, listvy's divIcon markers).
export const homeTypeIconSvg = (key, { strokeWidth = 1.8, className = '', style = '' } = {}) => (
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round"`
    + ` stroke-linejoin="round" aria-hidden="true"${className ? ` class="${className}"` : ''}${style ? ` style="${style}"` : ''}>`
    + `<path d="${pathFor(key)}"/></svg>`
);

// A plain <svg> (the filter's tabs).
export const HomeTypeIcon = ({ type, strokeWidth = 1.8, ...rest }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round"
        strokeLinejoin="round" aria-hidden="true" {...rest}>
        <path d={pathFor(type)} />
    </svg>
);

// An MUI SvgIcon, so it sizes and colours like the other MUI icons (the Färgkarta menu).
export const HomeTypeSvgIcon = ({ type, strokeWidth = 1.8, ...rest }) => (
    <SvgIcon {...rest}>
        <path d={pathFor(type)} fill="none" stroke="currentColor" strokeWidth={strokeWidth}
            strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
);

// Filled home shapes for the map markers (shapeMarkers.js), on the same 24×24 grid: each home with a chimney
// (flats: a roof edge and balconies instead), paned windows, an arched front door and a small round tree beside
// it. `body` is the white silhouette (home and tree; every subpath runs clockwise, so overlaps stay filled),
// `details` the windows, door and tree trunk, drawn in the type colour.
const rect = (x, y, w, h) => `M${x} ${y}h${w}v${h}h-${w}z`;
// A window of four panes, s × s.
const panes = (x, y, s) => {
    const pane = (s - 0.4) / 2;
    const step = pane + 0.4;
    return rect(x, y, pane, pane) + rect(x + step, y, pane, pane)
        + rect(x, y + step, pane, pane) + rect(x + step, y + step, pane, pane);
};
// A door w wide and h tall standing on `bottom`, with a round top.
const arch = (x, bottom, w, h) => `M${x} ${bottom}v-${h - (w / 2)}a${w / 2} ${w / 2} 0 0 1 ${w} 0v${h - (w / 2)}z`;
const circle = (cx, cy, r) => `M${cx - r} ${cy}a${r} ${r} 0 1 1 ${2 * r} 0a${r} ${r} 0 1 1 -${2 * r} 0z`;
const tree = (cx, cy, r, trunkBottom) => circle(cx, cy, r) + rect(cx - 0.5, cy + (r * 0.5), 1, trunkBottom - cy - (r * 0.5));
const trunk = (cx, cy, r, trunkBottom) => rect(cx - 0.3, cy + (r * 0.75), 0.6, trunkBottom - cy - (r * 0.75));
// A spruce (barrträd) for the fritidshus/cabin marker: three stacked clockwise triangles + a white trunk
// strip in the silhouette; the trunk is redrawn in the type colour via firTrunk (like tree/trunk).
const fir = (cx, top, base, hw) => {
    const span = base - top;
    const triangle = (apexY, baseY, w) => `M${cx} ${apexY}L${cx + w} ${baseY}L${cx - w} ${baseY}z`;
    return triangle(top, top + (span * 0.42), hw * 0.6)
        + triangle(top + (span * 0.26), top + (span * 0.7), hw * 0.82)
        + triangle(top + (span * 0.54), base, hw)
        + rect(cx - 0.5, base, 1, 2);
};
const firTrunk = (cx, base) => rect(cx - 0.4, base, 0.8, 2);
export const HOME_SHAPE_PATHS = {
    apartment: {
        body: 'M8 4.4h10V21H8z' + rect(7.2, 3, 11.6, 1.6) + tree(4.4, 16.4, 2.8, 21),
        details: [[9.6, 6.4], [14.2, 6.4], [9.6, 10], [14.2, 10], [9.6, 13.6], [14.2, 13.6]].map(([x, y]) => rect(x, y, 2.2, 2.2)).join('')
            + rect(9.1, 12.6, 3.2, 0.7) + rect(13.7, 12.6, 3.2, 0.7)
            + arch(11.9, 21, 2.2, 2.8) + trunk(4.4, 16.4, 2.8, 21),
    },
    house: {
        body: 'M2 12l7-6 3 2.571V5h1.5v4.857L16 12v8H2z' + tree(19.4, 13.4, 3, 20),
        details: panes(10.8, 12.8, 2.6) + arch(5.2, 20, 2.6, 3.8) + trunk(19.4, 13.4, 3, 20),
    },
    rowhouse: {
        body: 'M1 13l4.5-4.5L10 13l4.5-4.5 1.5 1.5V7.5h1.2v3.7L19 13v7H1z' + tree(21.2, 15.2, 2.3, 20),
        details: panes(2.3, 14.3, 2.2) + panes(11.3, 14.3, 2.2)
            + arch(6.2, 20, 2.2, 3.4) + arch(15.2, 20, 2.2, 3.4) + trunk(21.2, 15.2, 2.3, 20),
    },
    // A stuga: a steep-gable cottage with overhanging eaves and an arched door (no window, to tell it apart
    // from the villa), a spruce beside it.
    fritidshus: {
        body: 'M9 3L17 11L15 11L15 20L3 20L3 11L1 11z' + fir(20.6, 8, 19.2, 2.8),
        details: arch(9.2, 20, 2.6, 3.8) + firTrunk(20.6, 19.2),
    },
};
const shapeFor = (key) => HOME_SHAPE_PATHS[key] || HOME_SHAPE_PATHS.house;

// Halo widths in px at any size: the score colour, then a white gap, then the shape.
export const HOME_SHAPE_HALO_PX = 7;
export const HOME_SHAPE_GAP_PX = 3.4;

// SVG markup for listvy's DOM markers. size = the shape's box in px; the svg is padded for the halo
// and the count badge, and centred on the point.
export const homeShapeMarkerSvg = (key, { size, fill, halo, detail = '#fff', count = null, visited = false }) => {
    const pad = Math.ceil(HOME_SHAPE_HALO_PX / 2) + ((count != null || visited) ? 8 : 2);
    const box = size + (pad * 2);
    const k = size / 24;
    const shape = shapeFor(key);
    const bx = pad + (size * 0.92);
    const by = pad + (size * 0.08);
    const badge = count != null
        ? `<circle cx="${bx}" cy="${by}" r="7.5" fill="#262b2a" stroke="${halo}" stroke-width="1.6"/>`
            + `<text x="${bx}" y="${by + 3.2}" text-anchor="middle" font-size="9" font-weight="700" fill="#ffffff">${count}</text>`
        // Visited: a green check badge (single homes only, so it never shares the corner with a count).
        : (visited
            ? `<circle cx="${bx}" cy="${by}" r="6.5" fill="#1f7a62" stroke="#fff" stroke-width="1.4"/>`
                + `<path d="M${bx - 2.5} ${by + 0.1}L${bx - 0.8} ${by + 1.9}L${bx + 2.6} ${by - 2.1}" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
            : '');
    return `<svg width="${box}" height="${box}" viewBox="0 0 ${box} ${box}" aria-hidden="true" style="display:block;overflow:visible">`
        + `<g transform="translate(${pad} ${pad}) scale(${k})" stroke-linejoin="round">`
        + `<path d="${shape.body}" fill="${halo}" stroke="rgba(29,43,42,0.32)" stroke-width="${(HOME_SHAPE_HALO_PX + 1.4) / k}"/>`
        + `<path d="${shape.body}" fill="${halo}" stroke="${halo}" stroke-width="${HOME_SHAPE_HALO_PX / k}"/>`
        + `<path d="${shape.body}" fill="#fff" stroke="#fff" stroke-width="${HOME_SHAPE_GAP_PX / k}"/>`
        + `<path d="${shape.body}" fill="${fill}"/><path d="${shape.details}" fill="${detail}"/></g>${badge}</svg>`;
};

// Canvas paths for the shape markers (Path2D needs a browser, so they're made on first use).
let shapePaths = null;
export const homeShapePath2D = (key) => {
    if (!shapePaths) {
        shapePaths = Object.fromEntries(Object.entries(HOME_SHAPE_PATHS).map(([name, { body, details }]) => (
            [name, { body: new Path2D(body), details: new Path2D(details) }]
        )));
    }
    return shapePaths[key] || shapePaths.house;
};
